import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useProfileModal } from '../context/ProfileModalContext';
import { useFeedback } from '../context/FeedbackContext';
import { useStartOverModal } from '../context/StartOverModalContext';
import { useBackupModal } from '../context/BackupModalContext';
import ContentReader from './ContentReader';
import AboutModal from './AboutModal';
import InviteManagement from './InviteManagement';
import WorkshopHeaderLink from './WorkshopHeaderLink';
import LanguageSwitcher from './LanguageSwitcher';
import { useLocale } from '../context/LocaleContext';
import StepHelpPanel from './StepHelpPanel';
import styles from '../styles/header.module.css';

export interface BreadcrumbItem {
  label: string;
  path: string;
  active: boolean;
}

interface User {
  name: string;
  email: string;
  isAdmin?: boolean;
}

interface HeaderProps {
  breadcrumbs: BreadcrumbItem[];
  user?: User | null;
  onLogout: (onLogoutComplete?: () => void) => void;
  onLogin: () => void;
}

const Header: React.FC<HeaderProps> = ({ breadcrumbs, user, onLogout, onLogin }) => {
  const { openProfileModal } = useProfileModal();
  const { openFeedbackModal } = useFeedback();
  const { openBackupModal } = useBackupModal();
  const { openStartOverModal } = useStartOverModal();
  const { translationAccess, t } = useLocale();
  const [isPrivacyOpen, setIsPrivacyOpen] = useState(false);
  const [isAboutOpen, setIsAboutOpen] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [inviteManagementOpen, setInviteManagementOpen] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const getPageIdentifier = () => {
    const path = location.pathname;
    if (path.includes('/step-')) {
      const stepMatch = path.match(/\/step-(\d+)/);
      return stepMatch ? `Step ${stepMatch[1]}` : path;
    }
    if (path === '/') return 'Home';
    if (path === '/saa') return 'SAA Home';
    if (path === '/saa/12-steps') return '12 Steps';
    return path.replace('/', '').replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const handleMenuToggle = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleMenuClose = () => {
    setIsMenuOpen(false);
  };

  const handleProfileClick = () => {
    openProfileModal();
    handleMenuClose();
  };

  const handleFeedbackClick = () => {
    openFeedbackModal(getPageIdentifier());
    handleMenuClose();
  };

  const handleHelpClick = () => {
    setIsHelpOpen(true);
    handleMenuClose();
  };

  const handleStartOverClick = () => {
    openStartOverModal();
    handleMenuClose();
  };

  const handleBackupClick = () => {
    openBackupModal();
    handleMenuClose();
  };

  const handlePrivacyClick = () => {
    setIsPrivacyOpen(true);
    handleMenuClose();
  };

  const handleAboutClick = () => {
    setIsAboutOpen(true);
    handleMenuClose();
  };

  const handleInviteClick = () => {
    setInviteManagementOpen(true);
    handleMenuClose();
  };

  const handleLogoutClick = () => {
    onLogout(() => {
      // Navigate to home page after logout
      navigate('/');
    });
    handleMenuClose();
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  return (
    <header className={`app-header ${styles.header}`}>
      {/* Left: Breadcrumb Navigation */}
      <nav className={styles.breadcrumbs}>
        {breadcrumbs.map((item, index) => (
          <span key={index} className={styles.breadcrumbItem}>
            {index > 0 && <span className={styles.breadcrumbSeparator}> &gt; </span>}
            {item.active ? (
              <span className={styles.breadcrumbText}>{item.label}</span>
            ) : (
              <Link to={item.path} className={styles.breadcrumbLink}>
                {item.label}
              </Link>
            )}
          </span>
        ))}
      </nav>
      
      {/* Center: Site Title */}
      <div className={styles.siteTitle}>
        <Link to="/" className={styles.siteTitleLink}>
          <img 
            src="/recovery_commons_icon.png" 
            alt="Recovery Commons" 
            className={styles.siteLogo}
          />
          <span className={styles.siteTitleText}>Recovery Commons</span>
        </Link>
      </div>
      
      {/* Right: User Actions */}
      <div className={styles.userActions} ref={menuRef}>
        <LanguageSwitcher />
        <WorkshopHeaderLink userEmail={user?.email} />
        {user ? (
          <>
            <button
              onClick={handleMenuToggle}
              className={styles.hamburgerButton}
              title={t('nav.menu.title')}
              aria-label={t('nav.menu.toggle_aria')}
            >
              <div className={`${styles.hamburgerIcon} ${isMenuOpen ? styles.active : ''}`}>
                <span></span>
                <span></span>
                <span></span>
              </div>
            </button>
            
            {isMenuOpen && (
              <div className={styles.dropdownMenu}>
                <button 
                  onClick={handleProfileClick}
                  className={styles.menuItem}
                >
                  <svg 
                    width="16" 
                    height="16" 
                    viewBox="0 0 24 24" 
                    fill="currentColor"
                    className={styles.menuIcon}
                  >
                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                  </svg>
                  {t('nav.menu.profile')}
                </button>
                
                <button 
                  onClick={handleInviteClick}
                  className={styles.menuItem}
                >
                  <svg 
                    width="16" 
                    height="16" 
                    viewBox="0 0 24 24" 
                    fill="currentColor"
                    className={styles.menuIcon}
                  >
                    <path d="M15 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm-9-2V7H4v3H1v2h3v3h2v-3h3v-2H6zm9 4c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                  </svg>
                  {t('nav.menu.invite')}
                </button>
                
                <button 
                  onClick={handleFeedbackClick}
                  className={styles.menuItem}
                >
                  <svg 
                    width="16" 
                    height="16" 
                    viewBox="0 0 24 24" 
                    fill="currentColor"
                    className={styles.menuIcon}
                  >
                    <path d="M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z"/>
                  </svg>
                  {t('nav.menu.feedback')}
                </button>

                <button
                  onClick={handleHelpClick}
                  className={styles.menuItem}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    className={styles.menuIcon}
                  >
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z"/>
                  </svg>
                  {t('nav.menu.help')}
                </button>
                
                <button 
                  onClick={handleStartOverClick}
                  className={styles.menuItem}
                >
                  <svg 
                    width="16" 
                    height="16" 
                    viewBox="0 0 24 24" 
                    fill="currentColor"
                    className={styles.menuIcon}
                  >
                    <path d="M12 5v1.79c0 .45.54.67.85.35l2.79-2.79c.2-.2.2-.51 0-.71l-2.79-2.79c-.31-.31-.85-.09-.85.35V5H6c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2v-3h-2v3H6V8h6zm8 4h-7v2h4.59L13 16.59 14.41 18l6-6-6-6L13 7.41 17.59 12z"/>
                  </svg>
                  {t('nav.menu.start_over')}
                </button>
                
                <button 
                  onClick={handleBackupClick}
                  className={styles.menuItem}
                >
                  <svg 
                    width="16" 
                    height="16" 
                    viewBox="0 0 24 24" 
                    fill="currentColor"
                    className={styles.menuIcon}
                  >
                    <path d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96zM14 13v4h-4v-4H7l5-5 5 5h-3z"/>
                  </svg>
                  {t('nav.menu.backup')}
                </button>
                
                {user.isAdmin && (
                  <button 
                    onClick={() => {
                      window.location.href = '/admin';
                      handleMenuClose();
                    }}
                    className={styles.menuItem}
                  >
                    <svg 
                      width="16" 
                      height="16" 
                      viewBox="0 0 24 24" 
                      fill="currentColor"
                      className={styles.menuIcon}
                    >
                      <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/>
                    </svg>
                    Admin
                  </button>
                )}

                {translationAccess && (
                  <button
                    onClick={() => {
                      navigate('/translations');
                      handleMenuClose();
                    }}
                    className={styles.menuItem}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      className={styles.menuIcon}
                    >
                      <path d="M12.87 15.07l-2.54-2.51.03-.03A17.52 17.52 0 0 0 14.07 6H17V4h-7V2H8v2H1v2h11.17C11.5 7.92 10.44 9.75 9 11.35 8.07 10.32 7.3 9.19 6.69 8h-2c.73 1.63 1.73 3.17 2.98 4.56l-5.09 5.02L4 19l5-5 3.11 3.11.76-2.04zM18.5 10h-2L12 22h2l1.12-3h4.75L21 22h2l-4.5-12zm-2.62 7l1.62-4.33L19.12 17h-3.24z"/>
                    </svg>
                    Translations
                  </button>
                )}
                
                <div className={styles.menuDivider}></div>
                
                <button 
                  onClick={handleAboutClick}
                  className={styles.menuItem}
                >
                  <svg 
                    width="16" 
                    height="16" 
                    viewBox="0 0 24 24" 
                    fill="currentColor"
                    className={styles.menuIcon}
                  >
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                  </svg>
                  {t('nav.menu.about')}
                </button>
                
                <button 
                  onClick={handlePrivacyClick}
                  className={styles.menuItem}
                >
                  <svg 
                    width="16" 
                    height="16" 
                    viewBox="0 0 24 24" 
                    fill="currentColor"
                    className={styles.menuIcon}
                  >
                    <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/>
                  </svg>
                  {t('nav.menu.privacy')}
                </button>
                
                <button 
                  onClick={handleLogoutClick}
                  className={styles.menuItem}
                >
                  <svg 
                    width="16" 
                    height="16" 
                    viewBox="0 0 24 24" 
                    fill="currentColor"
                    className={styles.menuIcon}
                  >
                    <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z"/>
                  </svg>
                  {t('nav.menu.sign_out')}
                </button>
              </div>
            )}
          </>
        ) : (
          <button onClick={onLogin} className={styles.loginButton} title={t('nav.sign_in_create')}>
            <span className={styles.loginFull}>{t('nav.sign_in_create')}</span>
            <span className={styles.loginShort}>{t('nav.sign_in')}</span>
          </button>
        )}
      </div>
      
                        {/* ContentReader for Privacy Policy */}
                  <ContentReader
                    isOpen={isPrivacyOpen}
                    onClose={() => setIsPrivacyOpen(false)}
                    filePath="/content/Recovery-Commons-Privacy.md"
                    startTag=""
                    endTag=""
                    title={t('nav.privacy_title')}
                  />
                  
                  <AboutModal
                    isOpen={isAboutOpen}
                    onClose={() => setIsAboutOpen(false)}
                  />

                  <StepHelpPanel
                    isOpen={isHelpOpen}
                    onClose={() => setIsHelpOpen(false)}
                    stepKey="app"
                  />
                  
                  {/* Invite Management (send + history) */}
                  <InviteManagement 
                    isOpen={inviteManagementOpen}
                    onClose={() => setInviteManagementOpen(false)}
                  />
    </header>
  );
};

export default Header; 